import React from 'react';
import { Icon } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { formatDateWithYear } from './formatters';
import type { LifecyclePrediction } from './types';
import css from './DigitalTwinView.module.css';

const formatSignalValue = (value: string): string =>
  value.replace(/[-_]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

export type DigitalTwinHeaderProps = {
  prediction: LifecyclePrediction;
};

export const DigitalTwinHeader: React.FC<DigitalTwinHeaderProps> = ({ prediction }) => {
  const { t } = useTranslation();

  return (
    <header className={css.dashboardHeader}>
      <div>
        <p className={css.headerKicker}>
          <Icon className={css.headerKickerIcon} aria-hidden>
            insights
          </Icon>
          {t('digitalTwin.overline')}
        </p>
        <h2 className={css.headerTitle}>{t('digitalTwin.title')}</h2>
        <p className={css.headerSubtitle}>{t('digitalTwin.subtext')}</p>
      </div>
      <aside className={css.headerMetaRail} aria-label={t('digitalTwin.headerMetaAria')}>
        {prediction.modelVersion && (
          <div className={css.metaRow}>
            <span className={css.metaLabel}>{t('digitalTwin.model')}</span>
            <span className={css.metaValue}>{prediction.modelVersion}</span>
          </div>
        )}
        {prediction.confidenceLevel && (
          <div className={css.metaRow}>
            <span className={css.metaLabel}>{t('digitalTwin.confidence')}</span>
            <span className={css.metaValue}>{formatSignalValue(prediction.confidenceLevel)}</span>
          </div>
        )}
        {prediction.predictionTier && (
          <>
            <div className={css.metaDivider} aria-hidden />
            <div className={css.metaRow}>
              <span className={css.metaLabel}>{t('digitalTwin.predictionTier')}</span>
              <span className={css.metaValue}>{formatSignalValue(prediction.predictionTier)}</span>
            </div>
          </>
        )}
        {prediction.createdAt && (
          <>
            <div className={css.metaDivider} aria-hidden />
            <div className={css.metaRow}>
              <span className={css.metaLabel}>{t('digitalTwin.updated')}</span>
              <span className={css.metaValue}>{formatDateWithYear(prediction.createdAt)}</span>
            </div>
          </>
        )}
      </aside>
    </header>
  );
};
